import React from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { useAuth } from '@core/auth/AuthProvider.tsx';
import { useAuthStore } from '@core/store';
import { Refresh, ExitToApp } from '@mui/icons-material';
import { Role, ActiveLicenses } from '@core/types/index';
import { useParams } from 'react-router';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requiredRole?: Role;
    requiredLicense?: ActiveLicenses | ActiveLicenses[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole, requiredLicense }) => {
    const { user, isAuthenticated, isLoading, login } = useAuth();

    // Get license/role checking functions from auth store
    const { hasRole, hasLicense, hasShopAccess } = useAuthStore();
    const { id } = useParams<{ id: string }>();

    // Show loading spinner while checking authentication
    if (isLoading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                gap={2}
                sx={{
                    bgcolor: 'background.default',
                    color: 'text.primary'
                }}
            >
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary">
                    Authenticating...
                </Typography>
            </Box>
        );
    }

    // If not authenticated, show login page
    if (!isAuthenticated || !user) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                gap={3}
                sx={{
                    bgcolor: 'background.default',
                    color: 'text.primary',
                    textAlign: 'center',
                    px: 3
                }}
            >
                <Typography variant="h4" color="primary" gutterBottom>
                    Authentication Required
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
                    Please log in to access this application.
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={login}
                        startIcon={<ExitToApp />}
                        sx={{
                            px: 3,
                            py: 1.2,
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 600,
                            minWidth: '140px'
                        }}
                    >
                        Sign In
                    </Button>
                </Box>

                <Typography variant="caption" color="text.disabled" sx={{ mt: 2 }}>
                    Having trouble? Try the retry button to clear any stuck authentication state.
                </Typography>
            </Box>
        );
    }

    // Check role requirements (bitwise check)
    if (requiredRole !== undefined && !hasRole(requiredRole)) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                gap={2}
                sx={{
                    bgcolor: 'background.default',
                    color: 'text.primary',
                    textAlign: 'center',
                    px: 3
                }}
            >
                <Typography variant="h5" color="error" gutterBottom>
                    Access Denied
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    You don't have the required role to access this page.
                </Typography>
                <Typography variant="body2" color="text.disabled">
                    Required role: {requiredRole} | Your role: {user.role}
                </Typography>
                <Typography variant="body2" color="text.disabled">
                    Current user: {user.email}
                </Typography>

                <Button variant="outlined" onClick={() => window.history.back()} sx={{ mt: 2 }}>
                    Go Back
                </Button>
            </Box>
        );
    }

    // Check shop access requirements (bitwise check)
    if (id !== undefined && !hasShopAccess(id)) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                gap={2}
                sx={{
                    bgcolor: 'background.default',
                    color: 'text.primary',
                    textAlign: 'center',
                    px: 3
                }}
            >
                <Typography variant="h5" color="error" gutterBottom>
                    Access Denied
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    You don't have the required access to this shop.
                </Typography>
                <Typography variant="body2" color="text.disabled">
                    Required shop access ID: {id}
                </Typography>
                <Typography variant="body2" color="text.disabled">
                    Current user: {user.email}
                </Typography>

                <Button variant="outlined" onClick={() => window.history.back()} sx={{ mt: 2 }}>
                    Go Back
                </Button>
            </Box>
        );
    }

    // Check license requirements (bitwise check for multiple licenses)
    if (requiredLicense !== undefined && !hasLicense(requiredLicense)) {
        const licenseArray = Array.isArray(requiredLicense) ? requiredLicense : [requiredLicense];

        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                gap={2}
                sx={{
                    bgcolor: 'background.default',
                    color: 'text.primary',
                    textAlign: 'center',
                    px: 3
                }}
            >
                <Typography variant="h5" color="error" gutterBottom>
                    License Required
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    You don't have the required license to access this feature.
                </Typography>
                <Typography variant="body2" color="text.disabled">
                    Required licenses: {licenseArray.join(', ')} | Your licenses: {user.organisation?.activeLicenses}
                </Typography>
                <Typography variant="body2" color="text.disabled">
                    Current user: {user.email}
                </Typography>

                <Button variant="outlined" onClick={() => window.history.back()} sx={{ mt: 2 }}>
                    Go Back
                </Button>
            </Box>
        );
    }

    // User is authenticated and has required permissions
    return <>{children}</>;
};

export default ProtectedRoute;
